import React from 'react';
import { flowRight } from 'lodash/fp';
import { SnackbarProvider } from 'notistack';

import { BrowserRouter, Switch, Link } from 'react-router-dom';
import { withStyles } from '@material-ui/core/styles';
import withWidth, { isWidthUp } from '@material-ui/core/withWidth';
import Typography from '@material-ui/core/Typography';

import { isPaymentMethodSetup } from 'utils/user';
import { DismissButton } from 'ui/Snackbar';
import PageLoader from 'ui/PageLoader';
import AppMain from './AppMain';
import AppBarHeader from './appbar/AppBarHeader';
import Nav from './sidenav/Nav';
import Inactivity from './Inactivity';
import User from '../auth/user';
import { loadAppData } from '../utils/system';
import withRoot from '../withRoot';
import { AppContext } from './AppContext';
import { buildRoutes } from './Routes';

const getMissingPaymentMethodNotification = user => {
    if (!isPaymentMethodSetup(user)) {
        return (
            <>
                <Typography
                    variant="subtitle1"
                    color="inherit"
                    noWrap
                    style={{ paddingRight: '0.3em' }}
                >
                    Payment information for your account needs to be updated.
                    Please update your payment details in
                </Typography>
                <Typography
                    to={'/account/details/payment_method'}
                    component={Link}
                    variant="subtitle1"
                    color="inherit"
                    noWrap
                >
                    Account > Details
                </Typography>
            </>
        );
    }
    return null;
};

const styles = theme => ({
    root: {
        display: 'flex',
        position: 'absolute',
        width: '100vw',
        height: '100vh',
        '& strong': {
            fontWeight: 500,
        },
    },
    nav: {
        width: '240px',
    },
    snackbarVariantInfo: {
        background: theme.palette.secondary.main,
    },
});

class App extends React.Component {
    state = {
        open: true,
        mobileOpen: false,
        user: null,
        settings: null,
        breadcrumbs: [],
    };

    fetchUserData = () => {
        loadAppData().then(({ user, settings }) => {
            this.setState({
                user: User.create(user),
                settings,
            });
        });
    };

    componentDidMount() {
        this.fetchUserData();
    }

    handleDrawerOpen = () => {
        this.setState(state => {
            return {
                open: !state.open,
                mobileOpen: !state.mobileOpen,
            };
        });
    };

    handleActionMenuOpen = () => {
        this.setState(state => {
            return {
                open: !state.openAccountMenu ? true : state.open,
            };
        });
    };

    handleUpdateBreadcrumbs = breadcrumbs => {
        this.setState({ breadcrumbs });
    };

    getAppbarState = () => {
        if (isWidthUp('sm', this.props.width)) {
            return this.state.open;
        }
        return false;
    };

    render() {
        const { user } = this.state;
        if (!user) {
            return <PageLoader />;
        }
        const { classes } = this.props;
        const notification = getMissingPaymentMethodNotification(user);
        return (
            <SnackbarProvider
                autoHideDuration={5000}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}
                action={snackBarId => <DismissButton snackBarId={snackBarId} />}
                classes={{
                    variantInfo: classes.snackbarVariantInfo,
                }}
            >
                <BrowserRouter>
                    <AppContext.Provider
                        value={AppContext.create(
                            this.state.user,
                            this.state.settings,
                            this.fetchUserData
                        )}
                    >
                        <div className={classes.root}>
                            <AppBarHeader
                                open={this.getAppbarState()}
                                openClickDrawer={this.handleDrawerOpen}
                                breadcrumbs={this.state.breadcrumbs}
                                user={this.state.user}
                                notification={notification}
                            />

                            <Nav
                                className={classes.nav}
                                open={this.state.open}
                                mobileOpen={this.state.mobileOpen}
                                handleActionMenuOpen={this.handleActionMenuOpen}
                                handleDrawerOpen={this.handleDrawerOpen}
                            />
                            <AppMain
                                wide={!this.state.open}
                                hasNotificationBar={Boolean(notification)}
                            >
                                <Switch>
                                    {buildRoutes(
                                        this.state.user,
                                        this.handleUpdateBreadcrumbs
                                    )}
                                </Switch>
                            </AppMain>
                        </div>
                        <Inactivity />
                    </AppContext.Provider>
                </BrowserRouter>
            </SnackbarProvider>
        );
    }
}

export default withRoot(flowRight(withStyles(styles), withWidth())(App));
